/**
 * Single source of truth for sidepanel edge-activation sensitivity.
 *
 * One 1–5 user setting (`sidepanel.edge-sensitivity`) drives both the native
 * edge-push detector (grace period + push distance, applied on macOS and
 * Windows) and the renderer's pre-peek "approach" affordance. Keeping the whole
 * mapping here ensures the native feel and the on-screen hint never drift apart.
 */

export const EDGE_SENSITIVITY_MIN = 1
export const EDGE_SENSITIVITY_MAX = 5
export const DEFAULT_EDGE_SENSITIVITY = 3

export type EdgeSensitivityLevel = 1 | 2 | 3 | 4 | 5

export interface EdgeSensitivityConfig {
  /** Seconds to wait after arriving at the edge before a push can register. */
  gracePeriod: number
  /** Pixels of inward push needed to open the panel. */
  pushThreshold: number
  /**
   * Fraction of screen width over which the renderer slides the trigger in as
   * the cursor approaches the edge. `0` disables the pre-peek affordance — the
   * trigger only appears once the native peek fires.
   */
  approachZoneRatio: number
}

const CONFIG: Record<EdgeSensitivityLevel, EdgeSensitivityConfig> = {
  1: { gracePeriod: 0.5, pushThreshold: 100, approachZoneRatio: 0 },
  2: { gracePeriod: 0.3, pushThreshold: 75, approachZoneRatio: 0 },
  3: { gracePeriod: 0.15, pushThreshold: 50, approachZoneRatio: 0.05 },
  4: { gracePeriod: 0.07, pushThreshold: 25, approachZoneRatio: 0.15 },
  5: { gracePeriod: 0.03, pushThreshold: 10, approachZoneRatio: 0.3 },
}

/** Coerce any stored/legacy value into a valid level, falling back to default. */
export function clampEdgeSensitivity(level: number): EdgeSensitivityLevel {
  const rounded = Math.round(level)
  if (!Number.isFinite(rounded)) return DEFAULT_EDGE_SENSITIVITY
  return Math.min(EDGE_SENSITIVITY_MAX, Math.max(EDGE_SENSITIVITY_MIN, rounded)) as EdgeSensitivityLevel
}

export function getEdgeSensitivityConfig(level: number): EdgeSensitivityConfig {
  return CONFIG[clampEdgeSensitivity(level)]
}

/**
 * Onboarding default overrides: roles whose workflow benefits from a more eager
 * side panel start at a higher sensitivity. Returns `undefined` when the role
 * has no override (keep the global default).
 *
 * Keys MUST match the canonical role labels served by the onboarding role-options
 * config (`onboarding-role-options.json`, mirrored by `FALLBACK_ROLES` in
 * `useRoleAndIndustrySelection`). If a label is renamed there, update it here too:
 * an unrecognized role simply falls back to the global default (a benign no-op),
 * so a drifted key fails silently rather than loudly.
 */
const ROLE_SENSITIVITY_OVERRIDES: Record<string, EdgeSensitivityLevel> = {
  'HR/Recruiting': 5,
}

export function getRoleEdgeSensitivity(role: string | undefined): EdgeSensitivityLevel | undefined {
  if (!role) return undefined
  return ROLE_SENSITIVITY_OVERRIDES[role]
}
